import * as React from 'react'
import { cn } from '@/lib/utils'

type TabsContextValue = {
  readonly activeTab: string
  readonly setActiveTab: (tab: string) => void
}

const TabsContext = React.createContext<TabsContextValue | null>(null)

function useTabs(): TabsContextValue {
  const ctx = React.useContext(TabsContext)
  if (!ctx) throw new Error('useTabs must be used within <Tabs>')
  return ctx
}

type TabsProps = {
  readonly defaultValue: string
  readonly value?: string
  readonly onValueChange?: (value: string) => void
  readonly children: React.ReactNode
  readonly className?: string
}

function Tabs({ defaultValue, value, onValueChange, children, className }: TabsProps) {
  const [internalActive, setInternalActive] = React.useState(defaultValue)
  const activeTab = value ?? internalActive

  const setActiveTab = React.useCallback(
    (tab: string) => {
      setInternalActive(tab)
      onValueChange?.(tab)
    },
    [onValueChange],
  )

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={cn('flex flex-col', className)}>{children}</div>
    </TabsContext.Provider>
  )
}

type TabsListProps = {
  readonly children: React.ReactNode
  readonly className?: string
}

function TabsList({ children, className }: TabsListProps) {
  return (
    <div
      role="tablist"
      className={cn(
        'flex items-center border-b gap-0',
        className,
      )}
    >
      {children}
    </div>
  )
}

type TabsTriggerProps = {
  readonly value: string
  readonly children: React.ReactNode
  readonly className?: string
  readonly disabled?: boolean
}

function TabsTrigger({ value, children, className, disabled = false }: TabsTriggerProps) {
  const { activeTab, setActiveTab } = useTabs()
  const isActive = activeTab === value

  return (
    <button
      role="tab"
      type="button"
      disabled={disabled}
      aria-selected={isActive}
      onClick={() => !disabled && setActiveTab(value)}
      className={cn(
        'relative px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none',
        'text-muted-foreground hover:text-foreground',
        isActive && 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      {children}
    </button>
  )
}

type TabsContentProps = {
  readonly value: string
  readonly children: React.ReactNode
  readonly className?: string
}

function TabsContent({ value, children, className }: TabsContentProps) {
  const { activeTab } = useTabs()
  if (activeTab !== value) return null
  return (
    <div role="tabpanel" className={cn('focus:outline-none', className)}>
      {children}
    </div>
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
